'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { PLCTA } from 'components/performance-lab/PLCTA';
import { useOpenCallbackModal } from 'components/performance-lab/hooks';
import styles from './FestiveStickyCallback.module.css';

/**
 * Sticky callback bar, bottom centre of the festive V1–V3 pages (Figma 5461:7551): a white
 * pill-shaped bar with "Not sure which protocol?" and an outlined "Book a callback"
 * button. It takes the place of the plans' LONGEVITY / RECOVERY switcher. It rises in once
 * the Max Plan card has been scrolled past, and slips back down while the closing callback
 * panel or the footer is on screen, so there's never a second callback button competing
 * with it.
 */
export const FestiveStickyCallback: React.FC = () => {
  const t = useTranslations('performance-lab');
  const openCallback = useOpenCallbackModal();
  const [pastMax, setPastMax] = useState(false);
  const [endVisible, setEndVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    // Appears once the Max Plan card (the feature card) has scrolled up past the top of
    // the viewport; the hero is the fallback anchor.
    const anchor =
      document.querySelector('article[data-variant="feature"]') ??
      document.querySelector('[data-pl-hero]');
    const ends = Array.from(
      document.querySelectorAll('[data-festive-callback], footer'),
    );
    const visibleEnds = new Set<Element>();

    // Measured on scroll rather than with an IntersectionObserver: a jump straight past
    // the card (anchor link, fast fling) never crosses an intersection threshold.
    let raf = 0;
    const measure = () => {
      raf = 0;
      setPastMax(anchor ? anchor.getBoundingClientRect().bottom < 0 : true);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    const endObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visibleEnds.add(entry.target);
        else visibleEnds.delete(entry.target);
      });
      setEndVisible(visibleEnds.size > 0);
    });

    ends.forEach((el) => endObserver.observe(el));
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      endObserver.disconnect();
    };
  }, []);

  const shown = pastMax && !endVisible;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const className = 'festive-sticky-chatbot-offset';
    if (shown) document.body.classList.add(className);
    else document.body.classList.remove(className);
    return () => {
      document.body.classList.remove(className);
    };
  }, [shown]);

  return (
    <div
      className={styles.dock}
      data-festive-sticky
      data-shown={shown ? 'true' : 'false'}
    >
      <div className={styles.bar} aria-hidden={shown ? undefined : true}>
        <p className={styles.prompt}>
          {t('festive.versions.v2.callback.heading')}
        </p>
        <PLCTA
          type="button"
          $plVariant="white"
          className={`${styles.pill} pl-custom-type`}
          onClick={() => openCallback({ flow: 'pl_festive_callback' })}
          tabIndex={shown ? 0 : -1}
          data-buttontype="performance_lab_festive_sticky_callback"
        >
          {t('festive.versions.hero.cta')}
        </PLCTA>
      </div>
    </div>
  );
};
